/**
 * Router + image adapters for the Phase 7 block library.
 *
 * This is the SINGLE narrowly-scoped exception to portability Rule 2
 * (no `react-router-dom` in shared components). The adapter exists precisely
 * to centralize router coupling so block `.tsx` files stay router-free.
 *
 * At cutover into `reklaim-services/apps/marketing/`, this file becomes
 * one-line re-exports from `next/link` + `next/image`; block files do not
 * change. Reference: Section 3.7 of `.cursor/plans/phase-7-8-payload-prep.plan.md`.
 */

import { forwardRef } from 'react';
import { Link as RRLink } from 'react-router-dom';
import type { ImgHTMLAttributes, ReactNode } from 'react';

export interface BlockLinkProps {
  href: string;
  children: ReactNode;
  className?: string;
  'aria-label'?: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /**
   * When `true`, force the link to render as a plain `<a target="_blank"
   * rel="noopener noreferrer">` regardless of the URL scheme. Use for
   * relative URLs that should still open in a new tab (e.g. a CMS-supplied
   * subdomain that doesn't match the auto-detect heuristics).
   */
  forceExternal?: boolean;
}

const isAutoExternalHref = (href: string): boolean =>
  href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:');

const isHashHref = (href: string): boolean => href.startsWith('#');

/**
 * `BlockLink` uses `React.forwardRef` so it can be used as the child of
 * `<Button asChild>` (Radix's `Slot` forwards a ref to its single child).
 * Both branches of the rendered element are `<a>`, so the ref type is
 * `HTMLAnchorElement` -- `react-router-dom`'s `<Link>` also forwards refs
 * to its underlying `<a>`, so the contract is uniform.
 */
export const BlockLink = forwardRef<HTMLAnchorElement, BlockLinkProps>(
  function BlockLink(
    { href, children, className, forceExternal, ...rest },
    ref,
  ) {
    if (forceExternal || isAutoExternalHref(href)) {
      return (
        <a
          ref={ref}
          href={href}
          className={className}
          target="_blank"
          rel="noopener noreferrer"
          {...rest}
        >
          {children}
        </a>
      );
    }

    if (isHashHref(href)) {
      return (
        <a ref={ref} href={href} className={className} {...rest}>
          {children}
        </a>
      );
    }

    return (
      <RRLink ref={ref} to={href} className={className} {...rest}>
        {children}
      </RRLink>
    );
  },
);

export const BlockImage = forwardRef<HTMLImageElement, ImgHTMLAttributes<HTMLImageElement>>(
  function BlockImage(props, ref) {
    return <img ref={ref} loading="lazy" {...props} />;
  },
);
